import { roleLabelForCustomer, roleIconForCustomer } from '../helpers/role_display'

const ESCAPE_LOOKUP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

// friendly aliases that collapse to the same visual chip as a real role id.
// 'fleet-admin' is the customer-facing label we use for customer-admin AND
// tbn-admin; let help authors write `:role[fleet-admin]` without caring
// which internal variant a specific reader is. others can be added here
// as naming drifts.
const ROLE_CHIP_ALIASES = {
  'fleet-admin': 'customer-admin',
  'tech-support': 'admin',
  'support': 'admin',
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ESCAPE_LOOKUP[ch] || ch)
}

function escapeForRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getIndentLevel(line) {
  const match = String(line || '').match(/^(\s*)/)
  return match ? match[1].length : 0
}

function isTableDivider(line) {
  const normalized = String(line || '').trim()
  if (!normalized) return false
  return /^(\|\s*:?-{3,}:?\s*)+\|?$/.test(normalized)
}

function isTableRow(line) {
  const normalized = String(line || '').trim()
  return normalized.startsWith('|') && normalized.includes('|', 1)
}

function splitTableCells(line) {
  const normalized = String(line || '').trim()
  const withoutLeading = normalized.startsWith('|') ? normalized.slice(1) : normalized
  const withoutTrailing = withoutLeading.endsWith('|') ? withoutLeading.slice(0, -1) : withoutLeading
  return withoutTrailing.split('|').map(cell => cell.trim())
}

export function renderInlineMarkdown(text) {
  if (!text) return ''

  let processed = String(text)
  const placeholders = []
  const addPlaceholder = (html) => {
    const key = `__MD_PLACEHOLDER_${placeholders.length}__`
    placeholders.push({ key, html })
    return key
  }

  // Inline code first so markdown symbols inside code are preserved.
  processed = processed.replace(/`([^`]+)`/g, (_, content) => addPlaceholder(`<code>${escapeHtml(content)}</code>`))

  // images: ![alt](url) — allow same-origin paths and https URLs
  processed = processed.replace(/!\[([^\]]*)\]\(((?:\/|https:\/\/)[^)]+)\)/g, (_, alt, url) => {
    return addPlaceholder(`<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" class="md-inline-img" />`)
  })

  // Links: allowlisted schemes only; anything else renders as plain text
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, url) => {
    var safeLabel = escapeHtml(label)
    var safeUrl = escapeHtml(url.trim())

    if (/^tel:\+?[\d\s\-().]+$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-tel">\u{1F4DE}\u00A0' + safeLabel + '</a>')
    }
    if (/^sms:\+?[\d\s\-().]+$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-sms">\u{1F4AC}\u00A0' + safeLabel + '</a>')
    }
    if (/^help:[a-f0-9]+$/.test(url.trim())) {
      var docId = escapeHtml(url.trim().slice(5))
      return addPlaceholder('<a href="/help_center/' + docId + '" class="md-link md-link-help" data-help-article="true">\u2139\uFE0F\u00A0' + safeLabel + '</a>')
    }
    // fi-1432 tutorial pills: [Show me](tutorial:driver-payroll) renders
    // as an inline pill. a global click handler in app.vue catches the
    // click via data-tutorial-id and dispatches to the tutorials util,
    // which navigates to the home_route (if any) then kicks off the
    // fleet-assistant walk-through. unknown ids render the pill anyway —
    // the handler logs and no-ops.
    if (/^tutorial:[a-zA-Z0-9_-]+$/.test(url.trim())) {
      var tutorialId = escapeHtml(url.trim().slice('tutorial:'.length))
      return addPlaceholder(
        '<a href="#" class="md-link-tutorial" data-tutorial-id="' + tutorialId + '" role="button">' +
        '<span class="md-link-tutorial-glyph" aria-hidden="true">\u25B6</span>' +
        '<span class="md-link-tutorial-label">' + safeLabel + '</span>' +
        '</a>'
      )
    }
    if (/^\/[a-zA-Z0-9_\-/]*$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-nav" data-spa-nav="true">' + safeLabel + '</a>')
    }

    return escapeHtml(label)
  })

  // role chips: :role[admin] / :role[fleet-admin] / :role[driver] etc.
  // expand to a styled pill using the customer-facing label+icon from the
  // shared role_display helper. unknown ids render as literal text so a
  // typo like :role[drivr] doesn't silently disappear. authors: see
  // docs/help/global/reference/role_reference.md for the full role list.
  processed = processed.replace(/:role\[([a-zA-Z0-9_-]+)\]/g, (match, rawId) => {
    const id = String(rawId).toLowerCase()
    const resolved = ROLE_CHIP_ALIASES[id] || id
    const label = roleLabelForCustomer(resolved)
    const icon = roleIconForCustomer(resolved)
    if (!label) return escapeHtml(match) // unknown role — keep the literal so the typo is visible
    const iconHtml = icon ? ('<i class="fa ' + icon + '" aria-hidden="true"></i>') : ''
    return addPlaceholder(
      '<span class="md-role-chip md-role-chip--role-' + resolved + '">' +
      iconHtml + escapeHtml(label) +
      '</span>'
    )
  })

  // safe inline html pass-through. markdown traditionally allows raw
  // html, but our renderer escapes everything by default for XSS safety.
  // this whitelist covers the formatting tags toast-ui's WYSIWYG (and
  // copy-paste from external sources) tend to emit. tags must have no
  // attributes — anything with attributes falls through to escapeHtml so
  // it shows as literal source rather than executing. content inside is
  // re-rendered inline (so *bold* inside <em> still works).
  const SAFE_INLINE_TAGS = ['em', 'i', 'strong', 'b', 'u', 's', 'strike', 'del', 'ins', 'mark', 'sub', 'sup']
  SAFE_INLINE_TAGS.forEach((tag) => {
    const pattern = new RegExp('<' + tag + '>([\\s\\S]*?)</' + tag + '>', 'gi')
    processed = processed.replace(pattern, (_, content) => {
      return addPlaceholder('<' + tag + '>' + renderInlineMarkdown(content) + '</' + tag + '>')
    })
  })
  // standalone <br>: emitted by some sources for soft breaks. allow the
  // self-closing variants too.
  processed = processed.replace(/<br\s*\/?\s*>/gi, () => addPlaceholder('<br>'))

  processed = processed.replace(/\*\*(.+?)\*\*/g, (_, content) => addPlaceholder(`<strong>${escapeHtml(content)}</strong>`))
  processed = processed.replace(/(^|[\s(])\*([^*]+?)\*(?=[\s).,;:!?]|$)/g, (_, prefix, content) => {
    return `${prefix}${addPlaceholder(`<em>${escapeHtml(content)}</em>`)}`
  })

  let escaped = escapeHtml(processed)
  // iterate in reverse so outer placeholders expand first; their html
  // can reference earlier (inner) placeholder keys (e.g. **`code`** —
  // the bold's html is `<strong>__MD_PLACEHOLDER_0__</strong>`). a
  // forward pass would replace the inner key before it landed in the
  // text, leaving a literal `__MD_PLACEHOLDER_0__` in the rendered html.
  for (let i = placeholders.length - 1; i >= 0; i -= 1) {
    const { key, html } = placeholders[i]
    const pattern = new RegExp(escapeForRegExp(key), 'g')
    escaped = escaped.replace(pattern, html)
  }
  return escaped
}

export function renderMarkdownToHtml(text, options = {}) {
  if (!text) return ''

  const settings = {
    stripAssistantClosings: false,
    addLastElementClass: false,
    ...options,
  }

  const lines = String(text).replace(/\r\n/g, '\n').split('\n')
  const result = []

  let listStack = []
  let listContent = []
  let inCodeBlock = false
  let codeFenceLanguage = ''
  let codeLines = []

  const closeOneList = () => {
    const popped = listStack.pop()
    if (popped) {
      listContent.push(`</${popped.type}>`)
    }
  }

  const flushAllLists = () => {
    while (listStack.length > 0) {
      closeOneList()
    }
    if (listContent.length > 0) {
      result.push(listContent.join(''))
      listContent = []
    }
  }

  const closeListsForIndent = (indent) => {
    while (listStack.length > 0 && listStack[listStack.length - 1].indent > indent) {
      closeOneList()
    }
  }

  const ensureList = (type, indent) => {
    while (listStack.length > 0) {
      const current = listStack[listStack.length - 1]
      if (current.indent > indent) {
        closeOneList()
        continue
      }
      if (current.indent === indent && current.type !== type) {
        closeOneList()
        continue
      }
      break
    }
    const current = listStack.length > 0 ? listStack[listStack.length - 1] : null
    if (!current || current.indent < indent || current.type !== type) {
      listStack.push({ type, indent })
      listContent.push(`<${type}>`)
    }
  }

  const flushCodeBlock = () => {
    if (!inCodeBlock) return
    flushAllLists()
    const langClass = codeFenceLanguage ? ` class="language-${escapeHtml(codeFenceLanguage)}"` : ''
    result.push(`<pre><code${langClass}>${escapeHtml(codeLines.join('\n'))}</code></pre>`)
    inCodeBlock = false
    codeFenceLanguage = ''
    codeLines = []
  }

  let i = 0
  while (i < lines.length) {
    const originalLine = lines[i]
    const line = String(originalLine || '').trim()
    const indent = getIndentLevel(originalLine)

    const fenceMatch = line.match(/^```(\w+)?\s*$/)
    if (fenceMatch) {
      if (inCodeBlock) {
        flushCodeBlock()
      } else {
        flushAllLists()
        inCodeBlock = true
        codeFenceLanguage = (fenceMatch[1] || '').trim()
        codeLines = []
      }
      i += 1
      continue
    }

    if (inCodeBlock) {
      codeLines.push(String(originalLine || ''))
      i += 1
      continue
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      flushAllLists()
      const headerCells = splitTableCells(line).map(cell => renderInlineMarkdown(cell))
      const bodyRows = []
      i += 2
      while (i < lines.length && isTableRow(lines[i])) {
        const rowCells = splitTableCells(lines[i]).map(cell => renderInlineMarkdown(cell))
        bodyRows.push(rowCells)
        i += 1
      }

      const thead = `<thead><tr>${headerCells.map(cell => `<th>${cell}</th>`).join('')}</tr></thead>`
      const tbody = bodyRows.length > 0
        ? `<tbody>${bodyRows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}</tbody>`
        : ''
      result.push(`<table class="markdown-table">${thead}${tbody}</table>`)
      continue
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      flushAllLists()
      const level = headingMatch[1].length
      const headingText = renderInlineMarkdown(headingMatch[2])
      result.push(`<h${level}>${headingText}</h${level}>`)
      i += 1
      continue
    }

    const numberedMatch = line.match(/^(\d+)[.)]\s+(.+)$/)
    if (numberedMatch) {
      const content = renderInlineMarkdown(numberedMatch[2])
      closeListsForIndent(indent)
      ensureList('ol', indent)
      listContent.push(`<li>${content}</li>`)
      i += 1
      continue
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/)
    if (bulletMatch) {
      const content = renderInlineMarkdown(bulletMatch[1])
      closeListsForIndent(indent)
      ensureList('ul', indent)
      listContent.push(`<li>${content}</li>`)
      i += 1
      continue
    }

    // blockquote: collect consecutive `> text` lines into a single
    // <blockquote>. blank lines between `>` lines stay inside the same
    // quote — toast-ui's WYSIWYG serializes each quoted line with a
    // blank line between them, but visually they read as one stacked
    // block. lines are joined with <br> rather than separate <p> so
    // the rendered output matches the tight stacking the author saw
    // in the editor (paragraph margins would otherwise insert visible
    // gaps between every line).
    const quoteMatch = line.match(/^>\s?(.*)$/)
    if (quoteMatch) {
      flushAllLists()
      const quoteLines = []
      while (i < lines.length) {
        const curTrim = String(lines[i] || '').trim()
        if (curTrim === '') {
          // peek ahead: if a later non-blank line still starts with `>`,
          // stay inside the blockquote (skip the blank). otherwise end it.
          let j = i + 1
          while (j < lines.length && String(lines[j] || '').trim() === '') j += 1
          if (j >= lines.length || !/^>/.test(String(lines[j] || '').trim())) break
          i = j
          continue
        }
        const m = curTrim.match(/^>\s?(.*)$/)
        if (!m) break
        if (m[1].trim() !== '') quoteLines.push(m[1])
        i += 1
      }
      const innerHtml = quoteLines.map(l => renderInlineMarkdown(l)).join('<br>')
      result.push(`<blockquote class="markdown-blockquote"><p>${innerHtml}</p></blockquote>`)
      continue
    }

    if (!line) {
      i += 1
      continue
    }

    flushAllLists()
    result.push(`<p>${renderInlineMarkdown(line)}</p>`)
    i += 1
  }

  flushCodeBlock()
  flushAllLists()

  let html = result.join('')

  if (settings.stripAssistantClosings) {
    const closingPatterns = [
      /if you (?:need|require|want|would like|'d like)[^<]*?[!.]/gi,
      /(?:please )?let me know[^<]*?[!.]/gi,
      /(?:please )?feel free[^<]*?[!.]/gi,
      /i(?:'m| am) here (?:to help|if you)[^<]*?[!.]/gi,
      /is there anything else[^<]*?[!.]/gi,
      /don't hesitate to[^<]*?[!.]/gi,
    ]
    closingPatterns.forEach((pattern) => {
      html = html.replace(pattern, '')
    })
  }

  html = html.replace(/<p[^>]*>\s*<\/p>/gi, '')
  html = html.replace(/<p[^>]*>\s*&nbsp;\s*<\/p>/gi, '')
  html = html.trim()

  if (settings.addLastElementClass) {
    const blockElementRegex = /<(p|ul|ol|h[1-6]|table|pre)([^>]*)>/g
    const matches = [...html.matchAll(blockElementRegex)]
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1]
      const tagName = lastMatch[1]
      const attrs = lastMatch[2] || ''
      const className = tagName === 'p'
        ? 'last-paragraph'
        : (tagName.match(/^h[1-6]$/) ? 'last-heading' : 'last-element')
      let newTag = `<${tagName}`
      if (attrs.includes('class=')) {
        newTag += attrs
          .replace(/class="([^"]*)"/, `class="$1 ${className}"`)
          .replace(/class='([^']*)'/, `class='$1 ${className}'`)
      } else {
        newTag += `${attrs} class="${className}"`
      }
      newTag += '>'
      const before = html.substring(0, lastMatch.index)
      const after = html.substring(lastMatch.index + lastMatch[0].length)
      html = before + newTag + after
    }
  }

  return html
}
